// Type for translation object - using Record to avoid circular dependencies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationObject = any

/**
 * Translates a user relation status to the localized version
 * @param status - The status constant (e.g., "ACTIVE", "PENDING_APPROVAL_BY_TRAINEE")
 * @param t - Translation object
 * @returns Translated status
 */
export const translateUserRelationStatus = (status: string, t: TranslationObject): string => {
  const statusKey = status as keyof typeof t.userRelationStatus
  return t.userRelationStatus?.[statusKey] || status
}

/**
 * Translates a training set status to the localized version
 * @param status - The status constant (e.g., "DRAFT", "ACTIVE", "COMPLETED", "CANCELLED")
 * @param t - Translation object
 * @returns Translated status
 */
export const translateTrainingSetStatus = (status: string, t: TranslationObject): string => {
  const statusKey = status as keyof typeof t.trainingSetStatus
  return t.trainingSetStatus?.[statusKey] || status
}

/**
 * Calculates the display status for a training based on time and actual status
 * @param actualStatus - The actual status from backend
 * @param startTime - Training start time
 * @param endTime - Training end time
 * @returns Array of display status strings (e.g., ['MOVED', 'COMPLETED'] for past moved trainings)
 */
export const getTrainingDisplayStatus = (
  actualStatus: string,
  startTime: string,
  endTime: string
): string[] => {
  const now = new Date()
  const start = new Date(startTime)
  const end = new Date(endTime)

  // If status is ACTIVE, determine if it's completed (past) or upcoming (future)
  if (actualStatus === 'ACTIVE') {
    if (end < now) {
      return ['COMPLETED']
    } else if (start > now) {
      return ['UPCOMING']
    }
    return ['ACTIVE']
  }

  // For MOVED status, also include time-based status
  if (actualStatus === 'MOVED') {
    if (end < now) {
      return ['MOVED', 'COMPLETED']
    } else if (start > now) {
      return ['MOVED', 'UPCOMING']
    }
    return ['MOVED']
  }

  // For all other statuses, return as is
  return [actualStatus]
}

/**
 * Translates a training status to the localized version
 * @param status - The display status (result of getTrainingDisplayStatus)
 * @param t - Translation object
 * @returns Translated status
 */
export const translateTrainingStatus = (status: string, t: TranslationObject): string => {
  const statusKey = status as keyof typeof t.trainingStatus
  return t.trainingStatus?.[statusKey] || status
}
